import { useForm, Control, UseFormMethods, SubmitHandler } from 'react-hook-form';

import { useParams } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  GqlUpdateWebCustomizationSettingsV2Input,
  RadiusOption,
  ProductCardButtonOption,
  MobileAppFlair,
  MobileNavigationStyle,
} from 'types/graphql';

import useErnie from 'shared/hooks/use-ernie';
import { isAccentReadable, isCanvasTextReadable } from '@dutchie/common/utils/concierge-luminance';
import { useStores } from 'src/hooks/use-stores';
import { PreviewData } from '../components/preview';
import { useUpdateWebCustomizationSettingsV2, MenuCustomizationData } from '../data-access';
import { ThemeFormProps } from './theme.types';

type HandlePublish = ReturnType<UseFormMethods<FormValues>['handleSubmit']>;

type FormValues = {
  buttonsLinks: string;
  mobileBackgroundColor?: string;
  mobileBackgroundFlair?: MobileAppFlair;
  mobileButtonFlair?: MobileAppFlair;
  mobileDividerFlair?: MobileAppFlair;
  mobileLoadingFlair?: MobileAppFlair;
  mobileHeaderBackgroundColor?: string;
  mobileHeaderImageUrl?: string;
  mobileHeaderImageUrlV2?: string;
  mobileHeaderNavigationStyle?: MobileNavigationStyle;
  mobileBottomBarStyle?: MobileNavigationStyle;
  mobileNavigationBarActiveColor?: string;
  mobileNavigationBarBackgroundColor?: string;
  mobileNavigationBarInactiveColor?: string;
  mobileShowTabLabels?: boolean;
  mobileTabShowSpecials: boolean;
  mobileTabShowScanPay: boolean;
  mobileTabShowCart: boolean;
  mobileTabShowConcierge: boolean;
  mobileTabShowLoyalty: boolean;
  mobileTabLabelAccount?: string;
  mobileTabLabelCart?: string;
  mobileTabLabelConcierge?: string;
  mobileTabLabelLoyalty?: string;
  mobileTabLabelMore?: string;
  mobileTabLabelPay?: string;
  mobileTabLabelShop?: string;
  mobileTabLabelSpecials?: string;
  navBar: string;
  staffPickTag: string;
  discountTag: string;
  defaultRadius: RadiusOption;
  productCardButton?: ProductCardButtonOption;
  // react-hook-form returns these as undefined when the concierge section is
  // unmounted; the optional type reflects the actual runtime shape.
  conciergeAccentColor?: string;
  conciergeCanvasColor?: string;
};

type Input = Pick<GqlUpdateWebCustomizationSettingsV2Input, 'colors' | 'concierge' | 'mobile' | 'radius'>;

type UseThemeFormReturn = {
  control: Control<FormValues>;
  handlePublish: HandlePublish;
  handleReset: () => void;
  isDirty: boolean;
  previewData: PreviewData;
  applyAllInput: Input;
  afterApplyAll: () => void;
  resetConciergeTheme: () => void;
  /** Single derivation of "the concierge theming section is active" — drives
   * section visibility, the inline warning, and whether saves carry the
   * concierge group, so the UI and the payload can never disagree. */
  conciergeThemingEnabled: boolean;
  conciergeThemeAdvisory: string;
  /** Shared save gate: toasts the blocker and returns false when the
   * concierge theme has a data-integrity failure (half-set pair). Advisory
   * conditions never block — both persist paths (publish here, apply-to-all
   * in the form) run through it. */
  guardConciergeTheme: () => boolean;
};

// Colors save as a pair, so a half-set theme is a data-integrity failure and
// still blocks. Pure so it can be unit-tested without rendering the form.
export function getConciergeThemeBlocker(accentColor: string, canvasColor: string): string {
  if (!accentColor && !canvasColor) {
    return '';
  }

  if (!accentColor || !canvasColor) {
    return 'Select both an accent and a background color, or reset to the default theme.';
  }

  return '';
}

// Advisory only — these never block a save. Both conditions are
// independently fixable, so both messages surface rather than the first alone.
// The text advisory fires on the contrast we will actually render, not on a
// luminance band, so it never warns about a combination that reads fine.
export function getConciergeThemeAdvisories(accentColor: string, canvasColor: string): string[] {
  if (!accentColor || !canvasColor) {
    return [];
  }

  const advisories: string[] = [];

  if (!isCanvasTextReadable(canvasColor)) {
    advisories.push('This background may make the concierge’s text hard to read.');
  }

  if (!isAccentReadable(accentColor, canvasColor)) {
    advisories.push('This accent color may be hard to read against your background color.');
  }

  if (advisories.length) {
    advisories.push('You can undo this with “Reset to default theme”.');
  }

  return advisories;
}

function valueOrFallback(value: string | null | undefined, fallback: string): string {
  return value?.length ? value : fallback;
}

function useMergeDataWithFormValues(this: void, data: MenuCustomizationData, formValues: FormValues): PreviewData {
  const { buttonsLinks, navBar, staffPickTag, discountTag, defaultRadius, productCardButton } = formValues;

  return {
    ...data,
    colors: {
      buttonsLinks,
      navBar,
      staffPickTag,
      discountTag,
      productCardButton: productCardButton ?? ProductCardButtonOption.tinted,
    },
    mobile: {
      backgroundColor: valueOrFallback(formValues.mobileBackgroundColor, data.mobile?.backgroundColor ?? ''),
      backgroundFlair: formValues.mobileBackgroundFlair ?? data.mobile?.backgroundFlair ?? MobileAppFlair.none,
      buttonFlair: formValues.mobileButtonFlair ?? data.mobile?.buttonFlair ?? MobileAppFlair.none,
      dividerFlair: formValues.mobileDividerFlair ?? data.mobile?.dividerFlair ?? MobileAppFlair.none,
      loadingFlair: formValues.mobileLoadingFlair ?? data.mobile?.loadingFlair ?? MobileAppFlair.none,
      headerBackgroundColor: valueOrFallback(
        formValues.mobileHeaderBackgroundColor,
        data.mobile?.headerBackgroundColor ?? ''
      ),
      headerImageUrl: valueOrFallback(formValues.mobileHeaderImageUrl, data.mobile?.headerImageUrl ?? ''),
      headerImageUrlV2: valueOrFallback(formValues.mobileHeaderImageUrlV2, data.mobile?.headerImageUrlV2 ?? ''),
      headerNavigationStyle:
        formValues.mobileHeaderNavigationStyle ??
        data.mobile?.headerNavigationStyle ??
        MobileNavigationStyle.platformDefault,
      bottomBarStyle:
        formValues.mobileBottomBarStyle ?? data.mobile?.bottomBarStyle ?? MobileNavigationStyle.platformDefault,
      bottomBarActiveColor: valueOrFallback(
        formValues.mobileNavigationBarActiveColor,
        data.mobile?.bottomBarActiveColor ?? ''
      ),
      bottomBarBackgroundColor: valueOrFallback(
        formValues.mobileNavigationBarBackgroundColor,
        data.mobile?.bottomBarBackgroundColor ?? ''
      ),
      bottomBarInactiveColor: valueOrFallback(
        formValues.mobileNavigationBarInactiveColor,
        data.mobile?.bottomBarInactiveColor ?? ''
      ),
      showTabLabels: formValues.mobileShowTabLabels ?? false,
      tabDisplay: {
        showSpecials: formValues.mobileTabShowSpecials,
        showPay: formValues.mobileTabShowScanPay,
        showCart: formValues.mobileTabShowCart,
        showConcierge: formValues.mobileTabShowConcierge,
        showLoyalty: formValues.mobileTabShowLoyalty,
      },
      tabLabels: {
        account: valueOrFallback(formValues.mobileTabLabelAccount, data.mobile?.tabLabels?.account ?? ''),
        cart: valueOrFallback(formValues.mobileTabLabelCart, data.mobile?.tabLabels?.cart ?? ''),
        concierge: valueOrFallback(formValues.mobileTabLabelConcierge, data.mobile?.tabLabels?.concierge ?? ''),
        loyalty: valueOrFallback(formValues.mobileTabLabelLoyalty, data.mobile?.tabLabels?.loyalty ?? ''),
        more: valueOrFallback(formValues.mobileTabLabelMore, data.mobile?.tabLabels?.more ?? ''),
        pay: valueOrFallback(formValues.mobileTabLabelPay, data.mobile?.tabLabels?.pay ?? ''),
        shop: valueOrFallback(formValues.mobileTabLabelShop, data.mobile?.tabLabels?.shop ?? ''),
        specials: valueOrFallback(formValues.mobileTabLabelSpecials, data.mobile?.tabLabels?.specials ?? ''),
      },
    },
    radius: {
      default: defaultRadius,
    },
  };
}

function formatInput(values: FormValues, includeConcierge: boolean): Input {
  const input: Input = {
    colors: {
      buttonsLinks: values.buttonsLinks,
      navBar: values.navBar,
      staffPickTag: values.staffPickTag,
      discountTag: values.discountTag,
      productCardButton: values.productCardButton,
    },
    mobile: {
      backgroundColor: values.mobileBackgroundColor,
      backgroundFlair: values.mobileBackgroundFlair,
      buttonFlair: values.mobileButtonFlair,
      dividerFlair: values.mobileDividerFlair,
      loadingFlair: values.mobileLoadingFlair,
      headerBackgroundColor: values.mobileHeaderBackgroundColor,
      headerImageUrl: values.mobileHeaderImageUrl,
      headerImageUrlV2: values.mobileHeaderImageUrlV2,
      headerNavigationStyle: values.mobileHeaderNavigationStyle,
      bottomBarStyle: values.mobileBottomBarStyle,
      bottomBarActiveColor: values.mobileNavigationBarActiveColor,
      bottomBarBackgroundColor: values.mobileNavigationBarBackgroundColor,
      bottomBarInactiveColor: values.mobileNavigationBarInactiveColor,
      showTabLabels: values.mobileShowTabLabels,
      tabDisplay: {
        showSpecials: values.mobileTabShowSpecials,
        showPay: values.mobileTabShowScanPay,
        showCart: values.mobileTabShowCart,
        showConcierge: values.mobileTabShowConcierge,
        showLoyalty: values.mobileTabShowLoyalty,
      },
      tabLabels: {
        account: values.mobileTabLabelAccount,
        cart: values.mobileTabLabelCart,
        concierge: values.mobileTabLabelConcierge,
        loyalty: values.mobileTabLabelLoyalty,
        more: values.mobileTabLabelMore,
        pay: values.mobileTabLabelPay,
        shop: values.mobileTabLabelShop,
        specials: values.mobileTabLabelSpecials,
      },
    },
    radius: {
      default: values.defaultRadius,
    },
  };

  if (includeConcierge) {
    input.concierge = {
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      accentColorHex: values.conciergeAccentColor || null,
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      canvasColorHex: values.conciergeCanvasColor || null,
    };
  }

  return input;
}

export function useThemeForm({ data }: ThemeFormProps): UseThemeFormReturn {
  const {
    colors: { buttonsLinks, navBar, staffPickTag, discountTag, productCardButton },
    mobile,
    radius: { default: defaultRadius },
    concierge,
  } = data;

  const defaultValues = {
    buttonsLinks,
    navBar,
    defaultRadius,
    mobileBackgroundColor: mobile?.backgroundColor ?? '',
    mobileBackgroundFlair: mobile?.backgroundFlair ?? MobileAppFlair.none,
    mobileButtonFlair: mobile?.buttonFlair ?? MobileAppFlair.none,
    mobileDividerFlair: mobile?.dividerFlair ?? MobileAppFlair.none,
    mobileLoadingFlair: mobile?.loadingFlair ?? MobileAppFlair.none,
    mobileHeaderBackgroundColor: mobile?.headerBackgroundColor ?? buttonsLinks,
    mobileHeaderImageUrl: mobile?.headerImageUrl ?? '',
    mobileHeaderImageUrlV2: mobile?.headerImageUrlV2 ?? '',
    mobileHeaderNavigationStyle: mobile?.headerNavigationStyle ?? MobileNavigationStyle.platformDefault,
    mobileBottomBarStyle: mobile?.bottomBarStyle ?? MobileNavigationStyle.platformDefault,
    mobileNavigationBarActiveColor: mobile?.bottomBarActiveColor ?? '',
    mobileNavigationBarBackgroundColor: mobile?.bottomBarBackgroundColor ?? '',
    mobileNavigationBarInactiveColor: mobile?.bottomBarInactiveColor ?? '',
    mobileShowTabLabels: mobile?.showTabLabels ?? false,
    mobileTabShowCart: mobile?.tabDisplay?.showCart ?? true,
    mobileTabShowConcierge: mobile?.tabDisplay?.showConcierge ?? false,
    mobileTabShowScanPay: mobile?.tabDisplay?.showPay ?? true,
    mobileTabShowLoyalty: mobile?.tabDisplay?.showLoyalty ?? true,
    mobileTabShowSpecials: mobile?.tabDisplay?.showSpecials ?? false,
    mobileTabLabelAccount: mobile?.tabLabels?.account ?? '',
    mobileTabLabelCart: mobile?.tabLabels?.cart ?? '',
    mobileTabLabelConcierge: mobile?.tabLabels?.concierge ?? '',
    mobileTabLabelLoyalty: mobile?.tabLabels?.loyalty ?? '',
    mobileTabLabelMore: mobile?.tabLabels?.more ?? '',
    mobileTabLabelShop: mobile?.tabLabels?.shop ?? '',
    mobileTabLabelSpecials: mobile?.tabLabels?.specials ?? '',
    mobileTabLabelPay: mobile?.tabLabels?.pay ?? '',
    // Need to account for flag being off
    staffPickTag: staffPickTag ?? '',
    discountTag: discountTag ?? '',
    productCardButton,
    conciergeAccentColor: concierge?.accentColorHex ?? '',
    conciergeCanvasColor: concierge?.canvasColorHex ?? '',
  };

  const {
    control,
    formState: { isDirty },
    handleSubmit,
    reset,
    // eslint-disable-next-line @typescript-eslint/unbound-method
    setValue,
    // eslint-disable-next-line @typescript-eslint/unbound-method
    watch,
  } = useForm<FormValues>({ defaultValues });

  const { id: dispensaryId } = useParams<{ id: string }>();

  const showErnie = useErnie();
  const { UI } = useStores();
  const flags = useFlags();
  const updateData = useUpdateWebCustomizationSettingsV2();

  // Only send the concierge group when the theming section is active: the store
  // must have concierge enabled AND the rollout flag on. The flag decouples this
  // deploy from the Armageddon write-path deploy — while it's off, saves never
  // carry the concierge group, so nothing reaches Armageddon before it can
  // persist it. (When the section is hidden the fields never mount, so
  // react-hook-form reads them back as undefined; sending that would clear
  // stored theming.)
  const enableConciergeTheming = flags['ecomm.concierge.custom-theming.rollout'] ?? false;
  const conciergeThemingEnabled = Boolean(UI.dispensary.storeSettings?.enableConcierge) && enableConciergeTheming;

  const formValues = watch();

  // When the section is hidden its fields never register, so they fall back
  // to defaults seeded from *stored* data — the blocker and advisory must
  // stay inert or stored out-of-range values would block saves for admins
  // who can't see or fix the concierge fields.
  const conciergeThemeBlocker = conciergeThemingEnabled
    ? getConciergeThemeBlocker(formValues.conciergeAccentColor ?? '', formValues.conciergeCanvasColor ?? '')
    : '';

  const conciergeThemeAdvisory = conciergeThemingEnabled
    ? getConciergeThemeAdvisories(formValues.conciergeAccentColor ?? '', formValues.conciergeCanvasColor ?? '').join(
        ' '
      )
    : '';

  const guardConciergeTheme = (): boolean => {
    if (conciergeThemeBlocker) {
      showErnie(conciergeThemeBlocker, 'danger');
      return false;
    }

    return true;
  };

  const onSubmit: SubmitHandler<FormValues> = async (submittedValues) => {
    if (!guardConciergeTheme()) {
      return;
    }

    const input = formatInput(submittedValues, conciergeThemingEnabled);

    try {
      await updateData({
        variables: {
          dispensaryId,
          input,
        },
      });

      showErnie('Your theme has been updated', 'success');
      reset({ ...submittedValues });
    } catch (err) {
      showErnie('Something went wrong, please try again.', 'danger');
      console.error(err);
    }
  };

  const handlePublish = handleSubmit(onSubmit);

  const handleReset = (): void => reset();

  // Back to the stock concierge theme: clear both colors. Publishing
  // afterwards persists the removal — empty colors serialize to null in
  // formatInput. (The light/dark scheme isn't a form field — it derives
  // from the canvas everywhere the theme renders.)
  const resetConciergeTheme = (): void => {
    setValue('conciergeAccentColor', '', { shouldDirty: true });
    setValue('conciergeCanvasColor', '', { shouldDirty: true });
  };

  const previewData = useMergeDataWithFormValues(data, formValues);

  const applyAllInput = formatInput(formValues, conciergeThemingEnabled);
  const afterApplyAll = (): void => {
    reset({ ...formValues });
  };

  return {
    control,
    handlePublish,
    handleReset,
    isDirty,
    previewData,
    applyAllInput,
    afterApplyAll,
    resetConciergeTheme,
    conciergeThemingEnabled,
    conciergeThemeAdvisory,
    guardConciergeTheme,
  };
}
